import { GetServerSideProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'
import { VerticalNavDefaultPageTemplate } from '@/components/templates/VerticalNavDefaultPageTemplate'
import { getStaticStores, getUserAvatar, useStores } from '@/utils'
import { IQuestionBase, IUser, Language, UserStatus } from '@/types'
import { MyHead } from '@/components/molecules/MyHead'
import { AlertBanner } from '@/components/molecules/AlertBanner'
import { UserDetailHeader } from '@/components/organisms/UserDetailHeader'
import { ProfileSingleColumnContentTemplate } from '@/components/templates/ProfileSingleColumnContentTemplate'
import { QuestionList } from '@/components/organisms/QuestionList'
import { Button } from '@/components/atoms/Button'
import { PrimaryHeading } from '@/components/atoms/PrimaryHeading'
import styles from './index.module.scss'

type Props = {
  user: IUser
  staticUserQuestions: IQuestionBase[]
  staticHasNextPage: boolean
}

const NUM_OF_ITEMS = 100

const User: NextPage<Props> = observer(({ user, staticUserQuestions, staticHasNextPage }) => {
  const { t } = useTranslation(['users-[id]', 'companies-users-[id]'])
  const { users, viewer } = useStores()
  const userInstance = users.createUserInstance(user)
  const title = `${user?.name}'s posts`
  let description = ''
  if (user?.profile.bio) {
    description = `${user?.profile.bio.substring(0, 130)}${user?.profile.bio.length > 130 ? '...' : ''}`
  }

  // ISRで取得した配列より大きい場合、ページ送りの初回リクエストが実行されていることにする
  const hasDoneLoadMore = users.userQuestions[user.username]?.length > staticUserQuestions?.length

  const [isFetching, setIsFetching] = useState(false)

  const fetchUserQuestions = async () => {
    if (isFetching) {
      return
    }

    setIsFetching(true)
    // 最初のページ送りはISRで取得したものを捨てるので、200件取得する
    await users.fetchUserQuestions({
      username: user?.username,
      limit: hasDoneLoadMore ? NUM_OF_ITEMS : NUM_OF_ITEMS * 2,
      shouldRefresh: !hasDoneLoadMore,
    })
    setIsFetching(false)
  }

  // ブラウザ側での初期化時に Store に hasNextPage を設定
  useEffect(() => {
    users.updateHasNextUserQuestionsPage(user?.username, staticHasNextPage)
  }, [])

  useEffect(() => {
    if (!user || !userInstance) {
      return
    }

    if (viewer.isInitialized && viewer.isSignedIn) {
      userInstance.fetch()
    }
  }, [viewer.isInitialized, user])

  return (
    <VerticalNavDefaultPageTemplate>
      <MyHead title={title} description={description} image={getUserAvatar(user, true)} twitterCardType='summary' />
      <ProfileSingleColumnContentTemplate header={<UserDetailHeader user={userInstance} />} hasHeaderImage>
        {user?.status === UserStatus.FREEZE && (
          <AlertBanner location='pageTop' styleType='danger'>
            {t('このアカウントは利用規約違反等の理由によって凍結されています。')}
          </AlertBanner>
        )}
        <div className={styles.heading}>
          <PrimaryHeading backgroundText='Posts'>Posts</PrimaryHeading>
        </div>
        <div className={styles.content}>
          <QuestionList items={hasDoneLoadMore ? users.userQuestions[user?.username] : staticUserQuestions} />
        </div>
        {users.hasNextUserQuestionsPage[user?.username] && (
          <div className={styles.button}>
            <Button
              styleType='default'
              componentType='button'
              buttonSize='large'
              isLoading={isFetching}
              onClick={() => fetchUserQuestions()}
            >
              {t('+ Load More')}
            </Button>
          </div>
        )}
      </ProfileSingleColumnContentTemplate>
    </VerticalNavDefaultPageTemplate>
  )
})

export const getServerSideProps: GetServerSideProps = async ({ locale, params }) => {
  const { users, viewer } = getStaticStores()
  await viewer.changeLanguage(locale as Language)
  const username = params.id as string
  const [user, staticUserQuestions] = await Promise.all([
    await users.fetchUser({ username: params.id as string, limitOfReference: 1, limitOfQuestion: 1 }),
    await users.fetchUserQuestions({ username, limit: 100, shouldRefresh: true }),
  ])

  if (!user) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      user,
      staticUserQuestions,
      staticHasNextPage: users.hasNextUserQuestionsPage[username],
      ...(await serverSideTranslations(locale, ['global', 'users-[id]', 'companies-users-[id]', 'p'])),
    },
  }
}

export default User
